import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

/** Q&A names: trimmed (also U+3000), 1–120 characters (API §6). */
export const INQUIRY_NAME_MAX = 120;

export const validateInquiryName = (
  raw: string,
): { name: string; error: 'empty' | 'tooLong' | null } => {
  const name = raw.replace(/^[\s　]+|[\s　]+$/g, '');
  if (!name) return { name, error: 'empty' };
  if ([...name].length > INQUIRY_NAME_MAX) return { name, error: 'tooLong' };
  return { name, error: null };
};

export const useNameError = () => {
  const { t } = useTranslation();
  return (error: 'empty' | 'tooLong' | null) =>
    error === 'empty'
      ? t('v2.history.nameEmpty')
      : error === 'tooLong'
        ? t('v2.history.nameTooLong', { n: INQUIRY_NAME_MAX })
        : null;
};

interface RenameDialogProps {
  /** Opened over the Recent Q&As drawer: no second surround (kit). */
  nested?: boolean;
  open: boolean;
  initialName: string;
  onSave: (name: string) => Promise<boolean>;
  onClose: () => void;
}

/** Rename a Q&A; the form stays open with the error until the name is valid. */
export const RenameInquiryDialog = ({
  open,
  initialName,
  onSave,
  onClose,
  nested = false,
}: RenameDialogProps) => {
  const { t } = useTranslation();
  const errorText = useNameError();
  const [value, setValue] = useState(initialName);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<InputRef>(null);

  useEffect(() => {
    if (open) {
      setValue(initialName);
      setTried(false);
    }
  }, [open, initialName]);

  const { name, error } = validateInquiryName(value);
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    setTried(true);
    if (error) {
      input.current?.focus();
      return;
    }
    setBusy(true);
    const saved = await onSave(name);
    setBusy(false);
    if (saved) onClose();
  };
  const shownError = tried ? errorText(error) : null;

  return (
    <Modal
      open={open}
      mask={!nested}
      title={t('v2.history.renameQATitle')}
      width={460}
      onCancel={onClose}
      afterOpenChange={visible => visible && input.current?.select()}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('v2.common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={busy}
          onClick={() => void submit()}
        >
          {t('v2.common.save')}
        </Button>,
      ]}
    >
      <form onSubmit={submit} noValidate>
        <label
          htmlFor="rename-qa-name"
          style={{ display: 'block', marginBottom: 8 }}
        >
          {t('v2.common.nameLabel')}
        </label>
        <Input
          id="rename-qa-name"
          ref={input}
          value={value}
          onChange={event => setValue(event.target.value)}
          status={shownError ? 'error' : undefined}
          aria-invalid={!!shownError}
          aria-describedby={shownError ? 'rename-qa-error' : undefined}
          showCount={{
            formatter: ({ count }) => `${count} / ${INQUIRY_NAME_MAX}`,
          }}
          size="large"
        />
        {shownError ? (
          <p
            id="rename-qa-error"
            role="alert"
            style={{ color: 'var(--danger)', marginTop: 4 }}
          >
            {shownError}
          </p>
        ) : null}
      </form>
    </Modal>
  );
};

interface DeleteDialogProps {
  /** Opened over the Recent Q&As drawer: no second surround (kit). */
  nested?: boolean;
  open: boolean;
  name: string;
  /** The Q&A is generating: deleting it stops the generation (spec §3). */
  generating: boolean;
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
}

export const DeleteInquiryDialog = ({
  open,
  name,
  generating,
  onConfirm,
  onClose,
  nested = false,
}: DeleteDialogProps) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    const done = await onConfirm();
    setBusy(false);
    if (done) onClose();
  };

  return (
    <Modal
      open={open}
      mask={!nested}
      title={
        generating
          ? t('v2.history.deleteRunningTitle')
          : t('v2.history.deleteQATitle')
      }
      width={460}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} autoFocus>
          {t('v2.common.cancel')}
        </Button>,
        <Button
          key="delete"
          danger
          loading={busy}
          onClick={() => void confirm()}
        >
          {t('v2.common.del')}
        </Button>,
      ]}
    >
      <p>
        {generating
          ? t('v2.history.deleteRunningBody', { q: name })
          : t('v2.history.deleteQABody', { q: name })}
      </p>
    </Modal>
  );
};
